import { Dropdown } from '@lego/klik-ui';
import { type ComponentProps, useMemo } from 'react';
import { useWorkspaceAg2Applications } from '@/hooks/workspace';

type Props = {
  ag2ProductId: string | null | undefined;
  isDisabled?: boolean;
  includeEmptyOption?: boolean;
  onChange: (value: string | null) => void;
  placeholder?: string;
  selectedAg2AppId: string | null;
  width?: number | string;
};

const formatApplicationLabel = (application: { id: string; name: string }) =>
  application.name === application.id ? application.id : `${application.name} (${application.id})`;

export const Ag2AppIdSelect = ({
  ag2ProductId,
  isDisabled = false,
  includeEmptyOption = true,
  onChange,
  placeholder = 'Select AG 2.0 application',
  selectedAg2AppId,
  width,
}: Props) => {
  const { data, isLoading } = useWorkspaceAg2Applications(ag2ProductId);

  const applicationOptions = useMemo(() => {
    const applications = data?.applications ?? [];
    const optionsById = new Map(applications.map((application) => [application.id, application]));

    if (selectedAg2AppId && !optionsById.has(selectedAg2AppId)) {
      optionsById.set(selectedAg2AppId, {
        id: selectedAg2AppId,
        name: selectedAg2AppId,
      });
    }

    return [...optionsById.values()]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((application) => ({
        label: formatApplicationLabel(application),
        value: application.id,
      }));
  }, [data?.applications, selectedAg2AppId]);

  const dropdownOptions = [
    includeEmptyOption ? (
      <Dropdown.Option key="none" value="">
        None
      </Dropdown.Option>
    ) : null,
    ...applicationOptions.map((application) => (
      <Dropdown.Option key={application.value} value={application.value}>
        {application.label}
      </Dropdown.Option>
    )),
  ].filter(Boolean) as ComponentProps<typeof Dropdown>['children'];

  return (
    <Dropdown
      isDisabled={isDisabled || isLoading || !ag2ProductId}
      onChange={(value: string) => {
        onChange(value || null);
      }}
      placeholder={!ag2ProductId ? 'Missing product ID' : isLoading ? 'Loading...' : placeholder}
      size="sm"
      value={selectedAg2AppId ? [selectedAg2AppId] : []}
      width={width}
    >
      {dropdownOptions}
    </Dropdown>
  );
};
